import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import type { FirmsController } from "../../../api/src/firms/firms.controller";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { SecuritiesController } from "../../../api/src/securities/securities.controller";
import { NotificationContext } from "../Notifications";
import FormFieldError from "../components/FormFieldError";
import { withOverrideMarker } from "../components/OverrideMarker";
import SymbolField from "../components/SymbolField";
import { useAuthenticatedMutationAsync } from "../lib/api";
import { permissionAwareErrorNotification } from "../lib/errorNotifications";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import { useQuerySecurities } from "../lib/security";
import type { SecurityMasterRow } from "./SecurityMaster";

const ADV_CATEGORY_OPTIONS = [
  "Exchange-Traded Equity Securities",
  "Non-Exchange-Traded Equity Securities",
  "U.S. Government/Agency Bonds",
  "U.S. State and Local Government Bonds",
  "Foreign Government Bonds",
  "Corporate Bonds (Investment Grade)",
  "Corporate Bonds (Non-Investment Grade)",
  "Derivatives (Futures)",
  "Derivatives (Options)",
  "Derivatives (Swaps)",
  "Derivatives (Other)",
  "Securities of Investment Companies (Mutual Funds)",
  "Securities of Investment Companies (ETFs)",
  "Securities of Investment Companies (Hedge Funds)",
  "Securities of Investment Companies (Private Equity Funds)",
  "Securities of Investment Companies (Other Pooled Investment Vehicles)",
  "Repurchase Agreements",
  "Securities Lending Collateral",
  "Cash and Cash Equivalents",
  "Other",
];

type SecurityMasterFormData = {
  symbol: string;
  description?: string;
  category: string;
  cusip?: string;
  securityType?: string;
  advCategory?: string;
  is13f?: string; // String because it's a select field that gets converted to boolean
};

const schema = yup.object({
  symbol: yup.string().required("Symbol is required").label("Symbol"),
  description: yup.string().optional().label("Description"),
  category: yup.string().required("Category is required").label("Category"),
  cusip: yup.string().optional().label("CUSIP"),
  securityType: yup.string().optional().label("Security Type"),
  advCategory: yup.string().optional().label("ADV Category"),
  is13f: yup.string().optional().label("13-F"),
});

/**
 * The FastTrack datapoints the modal shows beside the form, in display order.
 * They are FastTrack's, not the firm's: never a form field and never part of
 * the override PUT.
 */
const FASTTRACK_DATAPOINTS = [
  { key: "sector", label: "Sector" },
  { key: "industry", label: "Industry" },
  { key: "country", label: "Country" },
] as const;

interface SecurityMasterModalProps {
  show: boolean;
  onHide: () => void;
  editingRow: SecurityMasterRow | null;
  existingSymbols: string[];
  onSave: () => void;
  /**
   * Set only by the 13-F filing page (APP-2170). The save then sends "13-F"
   * only when the Admin changed it or the security already carries a firm
   * 13-F override, and "ADV Category" only when the Admin changed it, so an
   * edit of another field does not freeze the reviewer's call or the default
   * ADV category into firm overrides. Without it the body is unchanged.
   */
  sendOverridesOnlyWhenChanged?: boolean;
  /**
   * Set by the App Security Master screen (APP-2018, R109 to R112 of
   * doc-security-master-01a15f1e98f9287b). Only "13-F" follows the rule of
   * `sendOverridesOnlyWhenChanged`; "ADV Category" is still sent on every save.
   */
  sendIs13fOnlyWhenChanged?: boolean;
  /**
   * The rows the screen lists. In add mode the modal has no `editingRow`, so
   * the 13-F override of a listed symbol is read from here.
   */
  listedRows?: SecurityMasterRow[];
}

const SecurityMasterModal = ({
  show,
  onHide,
  editingRow,
  existingSymbols,
  onSave,
  sendOverridesOnlyWhenChanged = false,
  sendIs13fOnlyWhenChanged = false,
  listedRows,
}: SecurityMasterModalProps) => {
  const notificationContext = useContext(NotificationContext);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);

  const isEditing = editingRow !== null;

  // Only the datapoints FastTrack gave a value for; a fund has none, and the
  // 13-F screen's editingRow does not carry them at all.
  const fastTrackDatapoints = FASTTRACK_DATAPOINTS.map(({ key, label }) => ({
    key,
    label,
    value: editingRow?.[key]?.trim() ?? "",
  })).filter(({ value }) => value !== "");

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    resetField,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<SecurityMasterFormData>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      symbol: "",
      description: "",
      category: "",
      cusip: "",
      securityType: "",
      advCategory: "",
      is13f: "false",
    },
  });

  // Watch for changes to set unsaved changes flag
  const watchedFields = watch();
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty, watchedFields]);

  // Watch for symbol changes to auto-populate other fields
  const watchedSymbol = watch("symbol");
  const { data: securities } = useQuerySecurities(
    watchedSymbol ? [watchedSymbol] : [],
  );

  // Auto-populate fields when symbol changes (only if fields are empty).
  //
  // APP-1443: this is what makes the 13-F review screen's Edit usable. That
  // caller builds its editingRow from a filing decision, which carries only
  // symbol, description, cusip and the reviewer's 13-F call, so Category —
  // required by the schema — along with Security Type and ADV Category arrive
  // empty and are filled here from the firm's resolved security record. The
  // "only if empty" guard is why the same effect does not disturb Security
  // Master, which passes a fully populated row.
  //
  // 13-F is deliberately not resolved from the security record the way the
  // other fields are: on the 13-F screen that field carries the reviewer's own
  // decision for this filing, and overwriting it would discard the decision.
  // The field keeps whatever the open/reset effect below put in it. Until
  // APP-1454 this effect did resolve it, and only the absence of `is_13f` from
  // every securities query kept that from overwriting a reviewer's call.
  useEffect(() => {
    if (watchedSymbol && securities && securities.length > 0) {
      const security = securities.find((s) => s.identifier === watchedSymbol);

      if (security) {
        // Only auto-populate if the fields are currently empty
        const currentValues = getValues();
        if (!currentValues.description) {
          setValue("description", security.description || "");
        }
        if (!currentValues.cusip) {
          setValue("cusip", security.cusip || "");
        }
        if (!currentValues.category) {
          setValue("category", security.category || "");
        }
        if (!currentValues.securityType) {
          setValue("securityType", security.securityType || "");
        }
        if (!currentValues.advCategory) {
          if (sendOverridesOnlyWhenChanged) {
            // APP-2170: the filled value becomes the field's default, so
            // `dirtyFields.advCategory` is set only by the Admin's own change
            // (react-hook-form recomputes every field against its default).
            resetField("advCategory", {
              defaultValue: security.advCategory || "",
            });
          } else {
            setValue("advCategory", security.advCategory || "");
          }
        }
      }
    }
  }, [
    watchedSymbol,
    securities,
    setValue,
    getValues,
    resetField,
    sendOverridesOnlyWhenChanged,
  ]);

  // Note: CUSIP uniqueness validation will be handled on the backend

  // Both lists are paginated at `/v1`; each select offers every row, so each
  // is read page by page.
  const { rows: assetCategories } = useAuthenticatedFetchAllPages<
    UnpackResponse<SecuritiesController["getAssetCategories"]>["data"][number]
  >("/v1/securities/asset-categories");

  const { rows: securityTypes } = useAuthenticatedFetchAllPages<
    UnpackResponse<SecuritiesController["getSecurityTypes"]>["data"][number]
  >("/v1/securities/security-types");

  const assetCategoryOptions = useMemo(
    () =>
      [...assetCategories]
        .sort((a, b) =>
          a.fasttrackCategory < b.fasttrackCategory
            ? -1
            : a.fasttrackCategory > b.fasttrackCategory
              ? 1
              : 0,
        )
        .map((assetCategory) => (
          <option
            key={assetCategory.fasttrackCategory}
            value={assetCategory.fasttrackCategory}
          >
            {assetCategory.fasttrackCategory}
          </option>
        )),
    [assetCategories],
  );

  const securityTypeOptions = useMemo(
    () =>
      securityTypes
        .map(({ name }) => name)
        .sort()
        .map((securityType) => (
          <option key={securityType} value={securityType}>
            {securityType}
          </option>
        )),
    [securityTypes],
  );

  const advCategoryOptions = useMemo(
    () =>
      ADV_CATEGORY_OPTIONS.map((advCategory) => (
        <option key={advCategory} value={advCategory}>
          {advCategory}
        </option>
      )),
    [],
  );

  // Note: Security Type, Description, and CUSIP are auto-populated from security data

  const updateSecurityAssetCategories = useAuthenticatedMutationAsync<
    SerializedObject<
      UnpackResponse<FirmsController["updateSecurityAssetCategoryOverrides"]>
    >
  >(
    // Published at `/v1` by APP-1563, with the read on the screen behind this
    // modal. The `/v1/securities/asset-categories` read above is a DIFFERENT
    // route — `SecuritiesController.getAssetCategories`, the FastTrack category
    // list.
    "/v1/firm/securities/asset-categories",
    async (
      data: Record<
        string,
        {
          category: string;
          cusip?: string;
          description?: string;
          securityType?: string;
          advCategory?: string;
          is13f?: boolean;
          mode: "INSERT" | "DELETE";
        }
      >,
    ) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const saveMutation = useMutation({
    mutationFn: async (formData: SecurityMasterFormData) => {
      // Symbol and category are required for submission
      if (!formData.symbol || !formData.category) {
        throw new Error("Symbol and Category are required");
      }

      const isIs13fOverride =
        editingRow?.isIs13fOverride ??
        listedRows?.find((row) => row.symbol === formData.symbol)
          ?.isIs13fOverride;
      const sendIs13f =
        !(sendOverridesOnlyWhenChanged || sendIs13fOnlyWhenChanged) ||
        dirtyFields.is13f ||
        isIs13fOverride;
      const sendAdvCategory =
        !sendOverridesOnlyWhenChanged || dirtyFields.advCategory;

      await updateSecurityAssetCategories({
        [formData.symbol]: {
          category: formData.category,
          cusip: formData.cusip,
          description: formData.description,
          securityType: formData.securityType,
          ...(sendAdvCategory && { advCategory: formData.advCategory }),
          ...(sendIs13f && { is13f: formData.is13f === "true" }),
          mode: "INSERT",
        },
      });
    },
    onSuccess: () => {
      notificationContext.pushNotification({
        id: "security-master-save",
        header: isEditing ? "Security Updated" : "Security Added",
        body: isEditing
          ? "Security category mapping has been updated"
          : "New security category mapping has been added",
        variant: "success",
      });
      setHasUnsavedChanges(false);
      onSave();
    },
    onError: (error: unknown) => {
      // APP-1327: a 403 means the user lacks the Admin role — surface a clear
      // permissions message instead of the generic, misleading failure copy.
      // Every other failure (network, 5xx, validation) keeps the generic copy.
      // The generic copy depends on add-vs-edit, so it's computed here and
      // passed to the shared helper.
      notificationContext.pushNotification(
        permissionAwareErrorNotification(
          error,
          "security-master-save",
          isEditing ? "Failed to Update Security" : "Failed to Add Security",
          isEditing
            ? "Failed to update security category mapping"
            : "Failed to add new security category mapping",
          "the Security Master",
        ),
      );
    },
  });

  const onSubmit = (data: SecurityMasterFormData) => {
    saveMutation.mutate(data);
  };

  const handleHide = () => {
    if (hasUnsavedChanges) {
      setShowDiscardWarning(true);
    } else {
      onHide();
    }
  };

  const handleDiscardChanges = () => {
    setShowDiscardWarning(false);
    setHasUnsavedChanges(false);
    reset();
    onHide();
  };

  // Reset form when modal opens or editing row changes
  useEffect(() => {
    if (show) {
      if (isEditing && editingRow) {
        reset({
          symbol: editingRow.symbol || "",
          description: editingRow.description || "",
          category: editingRow.category || "",
          cusip: editingRow.cusip || "",
          securityType: editingRow.securityType || "",
          advCategory: editingRow.advCategory || "",
          is13f:
            editingRow.is13f !== undefined && editingRow.is13f !== null
              ? editingRow.is13f.toString()
              : "false",
        });
      } else {
        reset({
          symbol: "",
          description: "",
          category: "",
          cusip: "",
          securityType: "",
          advCategory: "",
          is13f: "false",
        });
      }
      setHasUnsavedChanges(false);
    }
  }, [show, isEditing, editingRow, reset]);

  return (
    <>
      <Modal show={show} onHide={handleHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing
              ? "Edit Security Category Mapping"
              : "Add Security Category Mapping"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Symbol *</Form.Label>
                  <SymbolField
                    fieldName="symbol"
                    register={register}
                    control={control}
                    getValues={getValues}
                    disabled={isEditing} // Don't allow editing symbol
                  />
                  <FormFieldError field={errors.symbol} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("description")}
                    isInvalid={!!errors.description}
                    placeholder="Auto-populated after selecting symbol."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    {...register("category")}
                    isInvalid={!!errors.category}
                  >
                    <option value="">Select Category</option>
                    {assetCategoryOptions}
                  </Form.Select>
                  <FormFieldError field={errors.category} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cusip</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("cusip")}
                    isInvalid={!!errors.cusip}
                    maxLength={9}
                    style={{
                      textTransform: "uppercase",
                    }}
                    placeholder="Auto-populated after selecting symbol."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Security Type</Form.Label>
                  <Form.Select
                    {...register("securityType")}
                    isInvalid={!!errors.securityType}
                  >
                    <option value="">Select Security Type</option>
                    {securityTypeOptions}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ADV Category</Form.Label>
                  <Form.Select
                    {...register("advCategory")}
                    isInvalid={!!errors.advCategory}
                  >
                    <option value="">Select ADV Category</option>
                    {advCategoryOptions}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {withOverrideMarker("13-F", editingRow?.isIs13fOverride)}
                  </Form.Label>
                  <Form.Select
                    {...register("is13f")}
                    isInvalid={!!errors.is13f}
                  >
                    <option value="">Select 13-F</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Form.Select>
                  {/*
                    APP-1444: the marker is about the SECURITY, not about the
                    value in the select — opened from the 13-F review screen
                    this field holds the reviewer's own call for the filing
                    (see the auto-populate comment above), so the note has to
                    name what the override actually set.
                  */}
                  {editingRow?.isIs13fOverride && (
                    <Form.Text className="text-muted">
                      <span aria-hidden="true">* </span>
                      This security&apos;s 13-F status is set by a firm
                      override.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {isEditing && fastTrackDatapoints.length > 0 && (
              <Row data-testid="security-master-fasttrack-datapoints">
                {fastTrackDatapoints.map(({ key, label, value }) => (
                  <Col md={4} key={key}>
                    <dl className="mb-3">
                      <dt className="form-label">{label}</dt>
                      <dd className="mb-0">{value}</dd>
                    </dl>
                  </Col>
                ))}
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleHide}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                !getValues("symbol") ||
                !getValues("category") ||
                saveMutation.isPending
              }
            >
              {saveMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {isEditing ? "Updating..." : "Adding..."}
                </>
              ) : (
                <>{isEditing ? "Update" : "Add"}</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Discard Changes Warning Modal */}
      <Modal
        show={showDiscardWarning}
        onHide={() => setShowDiscardWarning(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Discard Changes?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You have unsaved changes. Do you want to discard your changes?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDiscardWarning(false)}
          >
            Keep Editing
          </Button>
          <Button variant="danger" onClick={handleDiscardChanges}>
            Discard Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SecurityMasterModal;
